import { useEffect, useState } from 'react';

export const useRecaptcha = () => {
  const [isV3Ready, setIsV3Ready] = useState(false);
  const [isV2Ready, setIsV2Ready] = useState(false);

  useEffect(() => {
    // Load reCAPTCHA v3 script
    const v3SiteKey = import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY;
    if (v3SiteKey && !document.querySelector('script[src*="recaptcha/api.js"]')) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${v3SiteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => setIsV3Ready(true);
      document.head.appendChild(script);
    }
  }, []);

  const executeV3 = (action: string = 'login'): Promise<string> => {
    return new Promise((resolve, reject) => {
      const v3SiteKey = import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY;
      
      if (!v3SiteKey) {
        reject(new Error('reCAPTCHA v3 site key not configured'));
        return;
      }

      if (!(window as any).grecaptcha) {
        reject(new Error('reCAPTCHA not loaded'));
        return;
      }

      (window as any).grecaptcha.ready(() => {
        (window as any).grecaptcha
          .execute(v3SiteKey, { action })
          .then((token: string) => resolve(token))
          .catch((error: any) => reject(error));
      });
    });
  };

  const loadV2 = (): Promise<void> => {
    return new Promise((resolve) => {
      const v2SiteKey = import.meta.env.VITE_RECAPTCHA_V2_SITE_KEY;
      
      if (!v2SiteKey) {
        resolve();
        return;
      }

      // If v3 script exists, remove it first
      const v3Script = document.querySelector('script[src*="recaptcha/api.js?render="]');
      if (v3Script) {
        v3Script.remove();
      }

      // Load v2 script
      if (!document.querySelector('script[src*="recaptcha/api.js"][src*="explicit"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaV2Load&render=explicit';
        script.async = true;
        script.defer = true;
        
        (window as any).onRecaptchaV2Load = () => {
          setIsV2Ready(true);
          resolve();
        };
        
        document.head.appendChild(script);
      } else {
        setIsV2Ready(true);
        resolve();
      }
    });
  };

  const renderV2 = (elementId: string): Promise<number> => {
    return new Promise((resolve, reject) => {
      const v2SiteKey = import.meta.env.VITE_RECAPTCHA_V2_SITE_KEY;
      
      if (!v2SiteKey) {
        reject(new Error('reCAPTCHA v2 site key not configured'));
        return;
      }

      if (!(window as any).grecaptcha) {
        reject(new Error('reCAPTCHA not loaded'));
        return;
      }

      try {
        const widgetId = (window as any).grecaptcha.render(elementId, {
          sitekey: v2SiteKey,
          size: 'normal',
        });
        resolve(widgetId);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getV2Response = (widgetId: number): string | null => {
    if (!(window as any).grecaptcha) {
      return null;
    }
    return (window as any).grecaptcha.getResponse(widgetId);
  };

  const resetV2 = (widgetId: number) => {
    if ((window as any).grecaptcha) {
      (window as any).grecaptcha.reset(widgetId);
    }
  };

  return {
    isV3Ready,
    isV2Ready,
    executeV3,
    loadV2,
    renderV2,
    getV2Response,
    resetV2,
  };
};
